'use client'

// React Imports
import { useEffect, useRef, useState } from 'react'

// MUI Imports
import Tooltip from '@mui/material/Tooltip'
import IconButton from '@mui/material/IconButton'
import Popper from '@mui/material/Popper'
import Fade from '@mui/material/Fade'
import Paper from '@mui/material/Paper'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import MenuList from '@mui/material/MenuList'
import MenuItem from '@mui/material/MenuItem'
import ListSubheader from '@mui/material/ListSubheader'

// Hook Imports
import { useSettings } from '@core/hooks/useSettings'
import { useTranslation } from '@/i18n'
import { LANGUAGES, LANG_REGIONS } from '@/i18n/dictionaries'
import type { Lang, LangRegion } from '@/i18n/dictionaries'

// Un'icona-globo per sezione, famiglia glyphs-poly (ratifica Luca 2026-08-07).
// La mappa è Record sull'union CHIUSA LangRegion: il continente nuovo che
// entrerà in dictionaries.ts non compila finché non ha il suo globo — e
// l'icona va AGGIUNTA ANCHE alla lista di bundle-icons-css.ts, la classe da
// sola non basta (stessa trappola delle bandiere, 2026-08-07).
const REGION_ICONS: Record<LangRegion, string> = {
  europe: 'glyphs-poly-globe-europe',
  northAmerica: 'glyphs-poly-globe-north-america',
  southAmerica: 'glyphs-poly-globe-south-america',
  asia: 'glyphs-poly-globe-asia'
}

// Prefisso a due lettere davanti al nome del continente (ratifica Luca
// 2026-08-07): «nel caso un utente debba orientarsi in una lingua che non
// conosce» — se la GUI e' finita in armeno, `(EU)` resta leggibile. Sta QUI
// e non nelle traduzioni di proposito: il codice deve essere IDENTICO in
// tutte le lingue, e 29 traduzioni potrebbero divergere. Convenzione alpha-2
// dei continenti (EU/AS/AF/NA/SA/OC); le Americhe unite prendono AM.
const REGION_CODES: Record<LangRegion, string> = {
  europe: 'EU',
  northAmerica: 'NA',
  southAmerica: 'SA',
  asia: 'AS'
}

const LanguageDropdown = () => {
  // States
  const [open, setOpen] = useState(false)
  const [tooltipOpen, setTooltipOpen] = useState(false)

  // Sezioni RICHIUDIBILI (ratifica Luca 2026-08-07, secondo giro: «pensavo
  // più a dover cliccare sulla sezione continente per vedere le lingue, così
  // non ho una lista lunga e confusionaria»). Stato = insieme delle sezioni
  // aperte; a ogni apertura del menu si riparte con la SOLA sezione della
  // lingua attiva espansa — il menu si presenta corto (tre intestazioni +
  // le lingue di casa tua) e dice comunque dove sei.
  const [expanded, setExpanded] = useState<Set<LangRegion>>(new Set())

  // Refs
  const anchorRef = useRef<HTMLButtonElement>(null)
  const selectedRef = useRef<HTMLLIElement>(null)

  // Hooks
  const { settings } = useSettings()
  const { t, lang, setLang } = useTranslation()

  // Con l'elenco che scorre, la voce corrente puo' nascere fuori dalla parte
  // visibile: all'apertura si espande la sua sezione e la si porta sotto gli
  // occhi, cosi' il menu dice sempre DOVE SEI prima ancora di farti scegliere.
  // `block: 'nearest'` non muove nulla quando e' gia' visibile; il raf attende
  // il render della sezione appena espansa.
  useEffect(() => {
    if (open) {
      const current = LANGUAGES.find(language => language.code === lang)

      setExpanded(new Set(current ? [current.region] : []))
      requestAnimationFrame(() => selectedRef.current?.scrollIntoView({ block: 'nearest' }))
    }
  }, [open, lang])

  const toggleRegion = (region: LangRegion) => {
    setExpanded(prev => {
      const next = new Set(prev)

      if (next.has(region)) {
        next.delete(region)
      }
      else {
        next.add(region)
      }

      return next
    })
  }

  const handleClose = () => {
    setOpen(false)
    setTooltipOpen(false)
  }

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen)
  }

  const handleLanguageSwitch = (next: Lang) => {
    handleClose()

    if (lang !== next) {
      setLang(next)
    }
  }

  return (
    <>
      <Tooltip
        title={t('language.tooltip')}
        onOpen={() => setTooltipOpen(true)}
        onClose={() => setTooltipOpen(false)}
        open={open ? false : tooltipOpen ? true : false}
      >
        <IconButton ref={anchorRef} onClick={handleToggle} className='text-textPrimary'>
          <i className='tabler-language' />
        </IconButton>
      </Tooltip>
      <Popper
        open={open}
        transition
        disablePortal
        placement='bottom-start'
        anchorEl={anchorRef.current}
        className='min-is-[160px] !mbs-3 z-[1]'
      >
        {({ TransitionProps, placement }) => (
          <Fade
            {...TransitionProps}
            style={{ transformOrigin: placement === 'bottom-start' ? 'left top' : 'right top' }}
          >
            <Paper className={settings.skin === 'bordered' ? 'border shadow-none' : 'shadow-lg'}>
              <ClickAwayListener onClickAway={handleClose}>
                {/* Scroll interno (Luca 2026-08-04): con diciassette lingue in
                    casa e ventisette in roadmap (#171) l'elenco intero copriva
                    lo schermo — e su un telefono usciva dalla viewport. Il
                    tetto è in `dvh` perché su mobile la barra dell'indirizzo
                    entra ed esce dal viewport: con `vh` l'ultima voce resta
                    sotto la piega proprio quando la barra è visibile. */}
                <MenuList onKeyDown={handleClose} className='max-bs-[60dvh] overflow-y-auto'>
                  {/* Sottosezioni per continente (ratifica Luca 2026-08-07):
                      l'ordine delle sezioni è LANG_REGIONS (europe → nord-/sud-america
                      → asia), le sezioni senza lingue non si rendono. flatMap
                      e NON Fragment: MenuList vuole i MenuItem come figli
                      DIRETTI per gestire selected/tabIndex — un Fragment li
                      nasconderebbe alla sua iterazione dei children. */}
                  {LANG_REGIONS.flatMap(region => {
                    const regionLanguages = LANGUAGES.filter(language => language.region === region)

                    if (regionLanguages.length === 0) {
                      return []
                    }

                    const isExpanded = expanded.has(region)

                    return [
                      // Intestazione CLICCABILE che apre/chiude la sezione
                      // (ratifica del secondo giro): caption attenuata in
                      // maiuscoletto con chevron di stato a destra. Il click
                      // NON chiude il menu (siamo dentro il ClickAwayListener)
                      // e non seleziona nulla. disableSticky perché con lo
                      // scroll interno un header appiccicoso coprirebbe la
                      // voce selezionata portata in vista da scrollIntoView.
                      <ListSubheader
                        key={region}
                        disableSticky
                        onClick={() => toggleRegion(region)}
                        role='button'
                        aria-expanded={isExpanded}
                        className='flex items-center gap-2 uppercase tracking-wide text-xs leading-8 text-textDisabled cursor-pointer select-none hover:text-textSecondary'
                      >
                        <i className={`${REGION_ICONS[region]} text-lg shrink-0`} aria-hidden='true' />
                        {`(${REGION_CODES[region]}) ${t(`langRegion.${region}`)}`}
                        <i
                          className={`${isExpanded ? 'tabler-chevron-down' : 'tabler-chevron-right'} text-base shrink-0 mis-auto`}
                          aria-hidden='true'
                        />
                      </ListSubheader>,

                      // Le lingue si rendono SOLO a sezione espansa — resa
                      // condizionale e non Collapse: MenuList vuole i MenuItem
                      // come figli DIRETTI per selected/tabIndex, e un wrapper
                      // di animazione glieli nasconderebbe.
                      ...(isExpanded ? regionLanguages : []).map(language => (
                        <MenuItem
                          key={language.code}
                          ref={lang === language.code ? selectedRef : undefined}
                          className='gap-2'
                          onClick={() => handleLanguageSwitch(language.code)}
                          selected={lang === language.code}
                        >
                          <i className={`${language.flag} text-xl shrink-0`} aria-hidden='true' />
                          {/* Colonna a larghezza MINIMA per il tag, non monospaziato: il
                              mono di sistema ha un'altezza-x piu' grande e a pari
                              font-size sembra sovradimensionato (Luca 2026-08-05). Con
                              `inline-block` + `min-is-` i nomi restano incolonnati per i
                              tag `xx-YY` (5 char), e il tag scende a text-xs perche' e'
                              informazione di servizio, non l'etichetta che si legge.
                              `min-is-` e non `is-` (Luca 2026-08-07): `sr-Latn-RS` (10
                              char) e' l'unico tag piu' lungo di 5 e con la larghezza
                              FISSA sbordava sul nome — la minima cresce solo per quella
                              riga, le altre restano allineate. `whitespace-nowrap`
                              perche' il tag non vada mai a capo. Il tag È il `code`
                              (task #171, identificatori BCP 47 pieni): non c'e' piu' un
                              campo `tag` separato da tenere allineato a mano. */}
                          <span className='inline-block min-is-[3.5rem] whitespace-nowrap text-sm text-textSecondary shrink-0'>
                            ({language.code})
                          </span>
                          <span>{language.name}</span>
                        </MenuItem>
                      ))
                    ]
                  })}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Fade>
        )}
      </Popper>
    </>
  )
}

export default LanguageDropdown
